import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type AppLanguage = 'de-DE' | 'en-EN';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly _translate = inject(TranslateService);

  readonly supportedLanguages: string[] = ['de-DE', 'en-EN'] as const;

  init(): void {
    // `navigator.language` is rarely exactly "de-DE"/"en-EN" in the wild
    // (real values include "de", "de-AT", "de-CH", "en-US", "en-GB", ...).
    // Match on the base language subtag instead of the full locale, so any
    // German-language browser reliably gets German, and everything else
    // falls back to English.
    const baseLang = navigator.language.split('-')[0].toLowerCase();
    const lang: AppLanguage = baseLang === 'de' ? 'de-DE' : 'en-EN';

    this._translate.setFallbackLang('en-EN');
    this._activate(lang);
  }

  switchLanguage(lang: AppLanguage): void {
    this._activate(lang);
  }

  get currentLang(): string {
    return this._translate.getCurrentLang();
  }

  private _activate(lang: AppLanguage): void {
    this._translate.use(lang);

    // Keep the `lang` attribute in sync with the actually-active locale —
    // it's set to a static "en" in index.html, which is wrong for every
    // German-speaking visitor and would otherwise never update.
    document.documentElement.lang = lang;
  }
}
